import React, { useEffect, useMemo, useState } from "react";
import {
  useValidateEnglishQuizBlueprintMutation,
} from "../../slices/englishQuizBlueprintApiSlice";

import BlueprintValidationSummary from "./components/BlueprintValidationSummary";
import BlueprintValidationResult from "./components/BlueprintValidationResult";

function getErrorMessage(error) {
  if (!error) {
    return "";
  }

  if (typeof error === "string") {
    return error;
  }

  return (
    error?.data?.message ||
    error?.data?.error ||
    error?.message ||
    "Unable to validate the blueprint."
  );
}

function getResult(response) {
  if (!response) {
    return null;
  }

  return (
    response?.data?.validation ||
    response?.data?.result ||
    response?.validation ||
    response?.result ||
    response?.data ||
    response
  );
}

function getBoolean(value) {
  if (typeof value === "boolean") {
    return value;
  }

  if (
    value === 1 ||
    value === "1" ||
    value === "true"
  ) {
    return true;
  }

  return false;
}

function getValidationState(result) {
  if (!result) {
    return "idle";
  }

  if (
    getBoolean(result.valid) ||
    getBoolean(result.isValid) ||
    getBoolean(result.success)
  ) {
    return "valid";
  }

  if (
    result.valid === false ||
    result.isValid === false ||
    result.success === false
  ) {
    return "invalid";
  }

  const status = String(
    result.status || ""
  ).toLowerCase();

  if (
    [
      "valid",
      "success",
      "passed",
      "pass",
      "ok",
    ].includes(status)
  ) {
    return "valid";
  }

  if (
    [
      "invalid",
      "failed",
      "fail",
      "error",
    ].includes(status)
  ) {
    return "invalid";
  }

  return "unknown";
}

function getIssues(result) {
  if (!result) {
    return [];
  }

  const collections = [
    result.errors,
    result.issues,
    result.validationErrors,
    result.problems,
    result.failures,
  ];

  for (const collection of collections) {
    if (Array.isArray(collection)) {
      return collection;
    }
  }

  return [];
}

function getWarnings(result) {
  if (!result) {
    return [];
  }

  const collections = [
    result.warnings,
    result.validationWarnings,
    result.warningMessages,
  ];

  for (const collection of collections) {
    if (Array.isArray(collection)) {
      return collection;
    }
  }

  return [];
}

function StatusBadge({ state }) {
  const config = {
    idle: {
      label: "Not validated",
      classes:
        "bg-gray-100 text-gray-600 ring-gray-500/20",
      dot: "bg-gray-400",
    },
    validating: {
      label: "Validating",
      classes:
        "bg-blue-50 text-blue-700 ring-blue-600/20",
      dot: "bg-blue-500",
    },
    valid: {
      label: "Valid",
      classes:
        "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
      dot: "bg-emerald-500",
    },
    invalid: {
      label: "Invalid",
      classes:
        "bg-red-50 text-red-700 ring-red-600/20",
      dot: "bg-red-500",
    },
    unknown: {
      label: "Needs review",
      classes:
        "bg-amber-50 text-amber-700 ring-amber-600/20",
      dot: "bg-amber-500",
    },
  };

  const item =
    config[state] || config.unknown;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset ${item.classes}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${item.dot}`}
      />
      {item.label}
    </span>
  );
}

function SummaryCard({
  label,
  value,
  description,
  color,
}) {
  const colors = {
    indigo:
      "border-indigo-100 bg-indigo-50 text-indigo-700",
    emerald:
      "border-emerald-100 bg-emerald-50 text-emerald-700",
    red:
      "border-red-100 bg-red-50 text-red-700",
    amber:
      "border-amber-100 bg-amber-50 text-amber-700",
  };

  return (
    <div
      className={`rounded-xl border p-4 ${
        colors[color] || colors.indigo
      }`}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide opacity-70">
        {label}
      </p>

      <p className="mt-1 text-2xl font-bold">
        {value}
      </p>

      {description && (
        <p className="mt-1 text-[10px] opacity-70">
          {description}
        </p>
      )}
    </div>
  );
}

function getCount(result, keys) {
  if (!result) {
    return 0;
  }

  for (const key of keys) {
    const value = result?.[key];

    if (
      typeof value === "number" &&
      Number.isFinite(value)
    ) {
      return value;
    }

    if (
      typeof value === "string" &&
      value.trim() !== "" &&
      !Number.isNaN(Number(value))
    ) {
      return Number(value);
    }
  }

  return 0;
}

export default function EnglishQuizBlueprintValidation({
  blueprintId,
  quizId,
  blueprint = null,
  initialResult = null,
  autoValidate = false,
  onValidated,
  onClose,
  className = "",
}) {
  const [
    validateBlueprint,
    {
      isLoading: isValidating,
      error: validationError,
    },
  ] =
    useValidateEnglishQuizBlueprintMutation();

  const [result, setResult] =
    useState(initialResult);

  const [hasValidated, setHasValidated] =
    useState(Boolean(initialResult));

  const validationState =
    isValidating
      ? "validating"
      : getValidationState(result);

  const issues = useMemo(
    () => getIssues(result),
    [result]
  );

  const warnings = useMemo(
    () => getWarnings(result),
    [result]
  );

  const errorCount = getCount(
    result,
    [
      "errorCount",
      "errorsCount",
      "totalErrors",
    ]
  );

  const warningCount = getCount(
    result,
    [
      "warningCount",
      "warningsCount",
      "totalWarnings",
    ]
  );

  const sectionCount = getCount(
    result,
    [
      "sectionCount",
      "sectionsCount",
      "totalSections",
    ]
  );

  const issueTotal =
    errorCount || issues.length;

  const warningTotal =
    warningCount || warnings.length;

  const resolvedBlueprintId =
    blueprintId ||
    blueprint?.id ||
    blueprint?._id ||
    blueprint?.blueprintId;

  const resolvedQuizId =
    quizId ||
    blueprint?.quizId;

  const executeValidation =
    async () => {
      if (
        !resolvedBlueprintId &&
        !resolvedQuizId
      ) {
        return;
      }

      try {
        const payload =
          resolvedBlueprintId
            ? {
                blueprintId:
                  resolvedBlueprintId,
              }
            : {
                quizId:
                  resolvedQuizId,
              };

        const response =
          await validateBlueprint(
            payload
          ).unwrap();

        const normalized =
          getResult(response);

        setResult(normalized);
        setHasValidated(true);

        if (onValidated) {
          onValidated(
            normalized,
            response
          );
        }

        return normalized;
      } catch (error) {
        setHasValidated(true);
        return null;
      }
    };

  useEffect(() => {
    if (
      autoValidate &&
      !initialResult &&
      (resolvedBlueprintId ||
        resolvedQuizId)
    ) {
      executeValidation();
    }
  }, [
    autoValidate,
    resolvedBlueprintId,
    resolvedQuizId,
  ]);

  useEffect(() => {
    if (initialResult) {
      setResult(initialResult);
      setHasValidated(true);
    }
  }, [initialResult]);

  const errorMessage =
    getErrorMessage(
      validationError
    );

  const isValid =
    validationState === "valid";

  const isInvalid =
    validationState === "invalid";

  return (
    <div
      className={`w-full space-y-5 ${className}`}
    >
      <div className="rounded-2xl border border-gray-200 bg-white">
        <div className="flex flex-col gap-4 border-b border-gray-200 px-5 py-5 sm:flex-row sm:items-start sm:justify-between sm:px-6">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg font-semibold text-gray-900">
                Blueprint Validation
              </h1>

              <StatusBadge
                state={validationState}
              />
            </div>

            <p className="mt-1 text-sm leading-6 text-gray-500">
              Validate the English quiz blueprint
              configuration before publishing or
              activating the quiz.
            </p>

            {blueprint && (
              <p className="mt-2 truncate text-xs font-medium text-gray-700">
                {blueprint.name ||
                  blueprint.title ||
                  "English Quiz Blueprint"}
              </p>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50"
              >
                Close
              </button>
            )}

            <button
              type="button"
              onClick={executeValidation}
              disabled={
                isValidating ||
                (!resolvedBlueprintId &&
                  !resolvedQuizId)
              }
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isValidating ? (
                <>
                  <svg
                    className="h-4 w-4 animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="9"
                      stroke="currentColor"
                      strokeWidth="3"
                    />
                    <path
                      className="opacity-90"
                      fill="currentColor"
                      d="M21 12a9 9 0 00-9-9v3a6 6 0 016 6h3z"
                    />
                  </svg>
                  Validating...
                </>
              ) : (
                <>
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="h-4 w-4"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.704 5.29a1 1 0 010 1.42l-7.2 7.2a1 1 0 01-1.414 0l-3.2-3.2a1 1 0 011.414-1.42l2.493 2.494 6.493-6.494a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {hasValidated
                    ? "Validate Again"
                    : "Validate Blueprint"}
                </>
              )}
            </button>
          </div>
        </div>

        {!resolvedBlueprintId &&
          !resolvedQuizId && (
            <div className="m-5 rounded-xl border border-amber-200 bg-amber-50 p-4 sm:m-6">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="h-4 w-4"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.721-1.36 3.486 0l6.58 11.69c.75 1.334-.213 2.985-1.743 2.985H3.42c-1.53 0-2.493-1.651-1.743-2.985l6.58-11.69zM10 7a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 7zm0 7.25a1 1 0 100-2 1 1 0 000 2z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>

                <div>
                  <p className="text-sm font-semibold text-amber-900">
                    Blueprint identifier required
                  </p>

                  <p className="mt-1 text-xs leading-5 text-amber-700">
                    Provide a blueprint ID or quiz ID to
                    validate the configuration.
                  </p>
                </div>
              </div>
            </div>
          )}

        {validationError && (
          <div className="m-5 rounded-xl border border-red-200 bg-red-50 p-4 sm:m-6">
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600">
                <svg
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="h-4 w-4"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.721-1.36 3.486 0l6.58 11.69c.75 1.334-.213 2.985-1.743 2.985H3.42c-1.53 0-2.493-1.651-1.743-2.985l6.58-11.69zM10 7a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 019 11.25v-3.5A.75.75 0 0110 7zm0 7.25a1 1 0 100-2 1 1 0 000 2z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              <div>
                <p className="text-sm font-semibold text-red-900">
                  Validation request failed
                </p>

                <p className="mt-1 text-xs leading-5 text-red-700">
                  {errorMessage}
                </p>
              </div>
            </div>
          </div>
        )}

        {hasValidated &&
          !isValidating &&
          result && (
            <div className="space-y-5 p-5 sm:p-6">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <SummaryCard
                  label="Status"
                  value={
                    isValid
                      ? "Valid"
                      : isInvalid
                        ? "Invalid"
                        : "Review"
                  }
                  description={
                    isValid
                      ? "Blueprint can proceed"
                      : "Blueprint requires attention"
                  }
                  color={
                    isValid
                      ? "emerald"
                      : isInvalid
                        ? "red"
                        : "amber"
                  }
                />

                <SummaryCard
                  label="Errors"
                  value={issueTotal}
                  description="Blocking validation issues"
                  color="red"
                />

                <SummaryCard
                  label="Warnings"
                  value={warningTotal}
                  description="Non-blocking warnings"
                  color="amber"
                />

                <SummaryCard
                  label="Sections"
                  value={
                    sectionCount ||
                    result?.sections?.length ||
                    "—"
                  }
                  description="Sections evaluated"
                  color="indigo"
                />
              </div>

              <BlueprintValidationSummary
                result={result}
              />

              <BlueprintValidationResult
                result={result}
              />

              {isValid && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                      <svg
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="h-4 w-4"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.704 5.29a1 1 0 010 1.42l-7.2 7.2a1 1 0 01-1.414 0l-3.2-3.2a1 1 0 011.414-1.42l2.493 2.494 6.493-6.494a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-emerald-900">
                        Blueprint validation passed
                      </p>

                      <p className="mt-1 text-xs leading-5 text-emerald-700">
                        The English quiz blueprint has no
                        blocking validation errors.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {warningTotal > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
                      <svg
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="h-4 w-4"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.721-1.36 3.486 0l6.58 11.69c.75 1.334-.213 2.985-1.743 2.985H3.42c-1.53 0-2.493-1.651-1.743-2.985l6.58-11.69zM10 7a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 019 11.25v-3.5A.75.75 0 0110 7zm0 7.25a1 1 0 100-2 1 1 0 000 2z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-amber-900">
                        Validation warnings found
                      </p>

                      <p className="mt-1 text-xs leading-5 text-amber-700">
                        Review the warnings before publishing
                        the English quiz.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

        {!hasValidated &&
          !isValidating &&
          !validationError && (
            <div className="px-5 py-12 text-center sm:px-6">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                <svg
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  className="h-6 w-6"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9.25 3.5H5.5A2.5 2.5 0 003 6v8.5A2.5 2.5 0 005.5 17h9a2.5 2.5 0 002.5-2.5v-3.75"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 3h4.5v4.5M16.25 3.75L9.5 10.5"
                  />
                </svg>
              </div>

              <h2 className="mt-4 text-sm font-semibold text-gray-900">
                Ready to validate
              </h2>

              <p className="mx-auto mt-1 max-w-md text-xs leading-5 text-gray-500">
                Run validation to check section configuration,
                question selection rules, marks, and other
                blueprint constraints.
              </p>

              <button
                type="button"
                onClick={executeValidation}
                disabled={
                  !resolvedBlueprintId &&
                  !resolvedQuizId
                }
                className="mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Validate Blueprint
              </button>
            </div>
          )}
      </div>
    </div>
  );
}